/**
 * @fileoverview HeaderSettings Component - User profile avatar and settings in header
 * 
 * Provides user profile functionality in the header including:
 * - Profile avatar display with initials fallback
 * - Profile image loading from API
 * - Navigation to profile page
 * - Auth0 integration for user data
 * - Retry logic for API initialization
 * 
 * @component
 * @example
 * <HeaderSettings />
 * 
 * @requires React
 * @requires @auth0/auth0-react
 * @requires react-router-dom
 * @requires ../../utils/apiHelper
 * 
 * @author By John Bellew - Infinia.ie - last updated 10/07/2025
 */

import React, { useState, useEffect, useContext, useCallback } from "react";
import { AuthContext } from "../Auth/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import apiHelper from "../../utils/apiHelper";
import { logout } from "../../utils/userJourneyHelper";
import Tippy from "@tippyjs/react";

/**
 * HeaderSettings Component
 * 
 * Renders user profile avatar and settings in the application header.
 * Handles profile image loading, user authentication status, and navigation
 * to the profile page. Provides fallback initials when profile image is unavailable.
 * 
 * Features:
 * - Profile avatar display with image loading
 * - Initials fallback for missing profile images
 * - Click navigation to profile page
 * - Retry logic for API initialization
 * - Profile image change event handling
 * 
 * @component
 * @returns {JSX.Element} Header settings component with user avatar
 */
function HeaderSettings() {
  const {
    user: [user],
  } = useContext(AuthContext);
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const fetchMemberProfile = useCallback(async () => {
    if (user?.profile?.email) {
      try {
        // Check if API is ready before making calls
        const authStatus = apiHelper.checkAuthStatus();
        if (!authStatus.tokenGetterInitialized) {
          // Retry after a short delay
          setTimeout(() => {
            setRetryCount((prev) => prev + 1);
          }, 1000);
          return;
        }

        const profile = await apiHelper.getMemberProfileByEmail(
          user.profile.email
        );

        if (profile?.id) {
          setProfileData(profile);
        }
      } catch (error) {
        console.error("Error fetching member profile:", error);
        if (error.message.includes("404") && retryCount < 3) {
          setTimeout(() => {
            setRetryCount((prev) => prev + 1);
          }, 2000);
        } else if (
          error.message.includes("Auth0 token getter not initialized") &&
          retryCount < 5
        ) {
          // Retry if auth isn't ready yet
          setTimeout(() => {
            setRetryCount((prev) => prev + 1);
          }, 1000);
        }
      }
    }
  }, [user?.profile?.email, retryCount]);

  useEffect(() => {
    fetchMemberProfile();
  }, [fetchMemberProfile]);

  useEffect(() => {
    const handleProfileImageChange = () => {
      fetchMemberProfile();
    };

    window.addEventListener("profileImageChanged", handleProfileImageChange);

    return () => {
      window.removeEventListener(
        "profileImageChanged",
        handleProfileImageChange
      );
    };
  }, [fetchMemberProfile]);

  useEffect(() => {
    const loadProfileImage = async () => {
      if (!profileData?.photo) return;

      try {
        // Check if API is ready before making calls
        const authStatus = apiHelper.checkAuthStatus();
        if (!authStatus.tokenGetterInitialized) {
          return;
        }

        const imageUrl = `${process.env.REACT_APP_API_URL}graph/getMediaContent/profile-images/${profileData.photo}`;
        const authToken = await apiHelper.getAuthToken();

        const response = await fetch(imageUrl, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });

        if (!response.ok) {
          if (response.status !== 404) {
            console.error("Error loading profile image:", response.statusText);
          }
          setProfileImage(null);
          return;
        }

        const blob = await response.blob();
        const imageObjectUrl = URL.createObjectURL(blob);
        setProfileImage(imageObjectUrl);
      } catch (error) {
        if (
          error.message &&
          !error.message.includes("404") &&
          !error.message.includes("Auth0 token getter not initialized")
        ) {
          console.error("Error loading profile image:", error);
        }
        setProfileImage(null);
      }
    };

    loadProfileImage();

    return () => {
      if (profileImage) {
        URL.revokeObjectURL(profileImage);
      }
    };
  }, [profileData?.photo]);

  const getInitials = (name) => {
    if (!name) return "";
    const nameParts = name.split(" ");
    const initials = nameParts
      .map((part) => part.charAt(0).toUpperCase())
      .join("");
    return initials;
  };

  const userName = user?.profile?.name || "";
  const initials = getInitials(userName);

  const handleClick = () => {
    if (profileData?.id) {
      navigate("/profile", {
        state: {
          profileData: profileData,
        },
      });
    }
  };

  return (
      <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
      {/* Logout Button - LEFT side */}
      {user ? (
        <Tippy content={"Logout"} placement="bottom">
          <div 
            className="logout-icon"
            onClick={() => logout()}
          >
            <svg>
              <use href="/assets/img/icons.svg#logout"></use>
            </svg>
          </div>
        </Tippy>
      ) : null}
      
      {/* Profile Avatar - RIGHT side */}
        <div
          title={userName}
          className="initials-avatar"
          onClick={handleClick}
          style={{
            cursor: profileData?.id ? "pointer" : "default",
          }}
        >
          {profileImage ? (
            <img
              src={profileImage}
              alt={userName}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
              onError={(e) => {
                e.target.style.display = "none";
                e.target.parentElement.textContent = initials;
              }}
            />
          ) : (
            initials
          )}
        </div>
      </div>
  );
}

export default HeaderSettings;
